<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const PADROES = {
  ritmo_humano: true,
  velocidade_digitacao: 45,
  baloes_maximos: 5,
  janela_de_rajada: 8,
  janela_de_rajada_maxima: 30,
};

const ritmoHumano = ref(PADROES.ritmo_humano);
const velocidade = ref(PADROES.velocidade_digitacao);
const baloes = ref(PADROES.baloes_maximos);
const janela = ref(PADROES.janela_de_rajada);
const janelaMaxima = ref(PADROES.janela_de_rajada_maxima);

const handleSubmit = () => {
  emit('submit', {
    config: {
      ...props.assistant.config,
      ritmo_humano: ritmoHumano.value,
      velocidade_digitacao:
        Number(velocidade.value) || PADROES.velocidade_digitacao,
      baloes_maximos: Number(baloes.value) || PADROES.baloes_maximos,
      janela_de_rajada: Number(janela.value) || 0,
      janela_de_rajada_maxima:
        Number(janelaMaxima.value) || PADROES.janela_de_rajada_maxima,
    },
  });
};

watch(
  () => props.assistant,
  novo => {
    const config = novo?.config || {};
    ritmoHumano.value =
      config.ritmo_humano === undefined
        ? PADROES.ritmo_humano
        : Boolean(config.ritmo_humano);
    velocidade.value =
      config.velocidade_digitacao ?? PADROES.velocidade_digitacao;
    baloes.value = config.baloes_maximos ?? PADROES.baloes_maximos;
    janela.value =
      config.janela_de_rajada === undefined
        ? PADROES.janela_de_rajada
        : config.janela_de_rajada;
    janelaMaxima.value =
      config.janela_de_rajada_maxima ?? PADROES.janela_de_rajada_maxima;
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <section class="flex flex-col gap-3">
      <div class="flex items-start gap-4">
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.DELIVERY.HUMAN_PACING.LABEL') }}
          </p>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.DELIVERY.HUMAN_PACING.HELP') }}
          </p>
        </div>
        <Switch v-model="ritmoHumano" />
      </div>

      <div v-if="ritmoHumano" class="grid gap-4 sm:grid-cols-2">
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.DELIVERY.SPEED.LABEL') }}
          </span>
          <input
            v-model="velocidade"
            type="number"
            min="10"
            max="300"
            class="w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12"
          />
          <span class="text-xs text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.DELIVERY.SPEED.HELP') }}
          </span>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.DELIVERY.BUBBLES.LABEL') }}
          </span>
          <input
            v-model="baloes"
            type="number"
            min="1"
            max="10"
            class="w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12"
          />
          <span class="text-xs text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.DELIVERY.BUBBLES.HELP') }}
          </span>
        </label>
      </div>
    </section>

    <span class="w-full h-px bg-n-weak" />

    <section class="flex flex-col gap-3">
      <div>
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.DELIVERY.BURST.LABEL') }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.FORM.DELIVERY.BURST.HELP') }}
        </p>
      </div>

      <div class="grid gap-4 sm:grid-cols-2">
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.DELIVERY.BURST.WINDOW') }}
          </span>
          <input
            v-model="janela"
            type="number"
            min="0"
            max="120"
            class="w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12"
          />
          <span class="text-xs text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.DELIVERY.BURST.WINDOW_HELP') }}
          </span>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.DELIVERY.BURST.MAX') }}
          </span>
          <input
            v-model="janelaMaxima"
            type="number"
            min="5"
            max="600"
            class="w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12"
          />
          <span class="text-xs text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.DELIVERY.BURST.MAX_HELP') }}
          </span>
        </label>
      </div>
    </section>

    <div class="flex justify-end">
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.DELIVERY.SAVE')"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
